/**
 * Mindvalley States - Shopify Theme JavaScript
 */

(function() {
  'use strict';

  // ==========================================
  // Utility Functions
  // ==========================================
  
  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  function isSubscriptionGift(item) {
    return Boolean(item.properties && item.properties._subscription_gift);
  }

  function formatMoney(cents) {
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(cents, window.Shopify.money_format || '${{amount}}');
    }
    return '$' + (cents / 100).toFixed(2);
  }

  // Centralised scroll-lock counter so cart, quiz modal, and exit-intent
  // popup don't trample each other when restoring scroll.
  let _scrollLockCount = 0;
  function lockScroll() {
    _scrollLockCount++;
    if (_scrollLockCount === 1) document.body.style.overflow = 'hidden';
  }
  function unlockScroll() {
    _scrollLockCount = Math.max(0, _scrollLockCount - 1);
    if (_scrollLockCount === 0) document.body.style.overflow = '';
  }
  // Exposed so other section-scoped scripts (e.g. header.liquid's mobile
  // menu) share the same ref-counted lock instead of directly setting
  // body.style.overflow, which would stomp an in-progress lock (like the
  // quiz modal's) held by another module.
  window.lockScroll = lockScroll;
  window.unlockScroll = unlockScroll;

  // Safely encode a value as text so it can be placed inside HTML strings
  // without XSS risk (replaces direct string interpolation into innerHTML).
  function sanitizeText(str) {
    if (str == null) return '';
    const el = document.createElement('span');
    el.textContent = String(str);
    return el.innerHTML;
  }

  // ==========================================
  // Segment Analytics Tracking
  // ==========================================
  
  const SegmentTracking = {
    /**
     * Format Shopify product data for Segment
     */
    formatProduct(item, source = null) {
      const product = {
        product_id: String(item.product_id || item.id || ''),
        sku: item.sku || item.variant_id || '',
        name: item.product_title || item.title || '',
        price: (item.price || 0) / 100, // Convert cents to dollars
        quantity: item.quantity || 1,
        category: item.product_type || 'Supplements',
        brand: 'Mindvalley States',
        image_url: item.image || item.featured_image || '',
        url: item.url || window.location.origin + '/products/' + (item.handle || '')
      };

      // Add variant info if available
      if (item.variant_title && item.variant_title !== 'Default Title') {
        product.variant = item.variant_title;
      }

      // Add source if provided (e.g., 'quiz', 'cross-sell')
      if (source) {
        product.source = source;
      }

      return product;
    },

    /**
     * Track Product Added to Cart
     */
    trackProductAdded(item, quantity, source = null) {
      if (typeof window.analytics === 'undefined') return;
      
      const product = this.formatProduct(item, source);
      product.quantity = quantity;

            
      window.analytics.track('Product Added', product);
    },

    /**
     * Track Product Removed from Cart
     */
    trackProductRemoved(item, quantity) {
      if (typeof window.analytics === 'undefined') return;
      
      const product = this.formatProduct(item);
      product.quantity = quantity;

            
      window.analytics.track('Product Removed', product);
    },

    /**
     * Track Cart Viewed
     */
    trackCartViewed(cart) {
      if (typeof window.analytics === 'undefined') return;
      
      const products = cart.items.map(item => this.formatProduct(item));
      
      const eventData = {
        cart_id: cart.token || String(Date.now()),
        products: products,
        value: cart.total_price / 100 // Convert cents to dollars
      };

            
      window.analytics.track('Cart Viewed', eventData);
    },

    /**
     * Track Checkout Started
     */
    trackCheckoutStarted(cart) {
      if (typeof window.analytics === 'undefined') return;
      
      const products = cart.items.map(item => this.formatProduct(item));
      
      const eventData = {
        order_id: cart.token || String(Date.now()),
        affiliation: 'Mindvalley States Store',
        value: cart.total_price / 100,
        revenue: cart.total_price / 100,
        currency: 'USD',
        products: products
      };

            
      window.analytics.track('Checkout Started', eventData);
    },

    /**
     * Track Product Viewed
     */
    trackProductViewed(product) {
      if (typeof window.analytics === 'undefined') return;
      
      const eventData = {
        product_id: String(product.id || ''),
        sku: product.sku || product.selected_or_first_available_variant?.sku || '',
        name: product.title || '',
        price: (product.price || 0) / 100,
        category: product.type || 'Supplements',
        brand: 'Mindvalley States',
        image_url: product.featured_image || '',
        url: product.url || window.location.href
      };

            
      window.analytics.track('Product Viewed', eventData);
    },

    /**
     * Track Product List Viewed
     */
    trackProductListViewed(listId, category, products) {
      if (typeof window.analytics === 'undefined') return;
      
      const productList = products.map(product => ({
        product_id: String(product.id || ''),
        sku: product.sku || '',
        name: product.title || '',
        price: (product.price || 0) / 100,
        category: product.type || 'Supplements',
        brand: 'Mindvalley States',
        image_url: product.featured_image || '',
        url: product.url || '',
        position: product.position || 0
      }));

      const eventData = {
        list_id: listId,
        category: category,
        products: productList
      };

            
      window.analytics.track('Product List Viewed', eventData);
    },

    /**
     * Track Quiz Started
     */
    trackQuizStarted(quizId, quizName) {
      if (typeof window.analytics === 'undefined') return;
      
      const eventData = {
        quiz_id: quizId,
        quiz_name: quizName,
        timestamp: new Date().toISOString()
      };

            
      window.analytics.track('Quiz Started', eventData);
    },

    /**
     * Track Quiz Completed
     */
    trackQuizCompleted(quizId, quizName, result, recommendations, scores) {
      if (typeof window.analytics === 'undefined') return;
      
      const eventData = {
        quiz_id: quizId,
        quiz_name: quizName,
        result: result,
        recommended_products: recommendations,
        score_breakdown: scores,
        timestamp: new Date().toISOString()
      };

            
      window.analytics.track('Quiz Completed', eventData);
    }
  };

  // ==========================================
  // Cart Functionality
  // ==========================================
  
  const Cart = {
    init() {
      this.cartToggle = document.getElementById('cart-toggle');
      this.cartDrawer = document.getElementById('cart-drawer');
      this.cartOverlay = document.getElementById('cart-overlay');
      this.cartClose = document.getElementById('cart-close');
      this.cartItems = document.getElementById('cart-items');
      this.cartTotal = document.getElementById('cart-total');
      this.headerCartCount = document.querySelector('[data-cart-count]');

      this.bindEvents();
      this.refresh(); // Load cart on init
    },

    bindEvents() {
      // Open cart
      if (this.cartToggle) {
        this.cartToggle.addEventListener('click', () => this.open());
      }
      
      // Close cart
      if (this.cartClose) {
        this.cartClose.addEventListener('click', () => this.close());
      }
      if (this.cartOverlay) {
        this.cartOverlay.addEventListener('click', () => this.close());
      }

      // Close on escape
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
      });

      // Add to cart — use event delegation so dynamically-injected buttons
      // (quiz results, cross-sell, etc.) are handled without re-binding.
      document.addEventListener('click', (e) => {
        const btn = e.target.closest('.add-to-cart-btn, .add-to-cart, .cross-sell-btn, .product-btn, [data-add-to-cart]');
        if (!btn) return;
        e.preventDefault();
        e.stopPropagation();
        const form = btn.closest('form');
        if (form) {
          this.addToCart(form);
        } else {
          const variantId = btn.dataset.variantId || btn.dataset.productId;
          const quantity = parseInt(btn.dataset.quantity) || 1;
          if (variantId) {
            this.addToCartById(variantId, quantity);
          }
        }
      });

      // Quantity buttons in cart
      if (this.cartItems) {
        this.cartItems.addEventListener('click', (e) => {
          if (e.target.classList.contains('qty-increase')) {
            this.updateQuantity(e.target.dataset.key, 1);
          } else if (e.target.classList.contains('qty-decrease')) {
            this.updateQuantity(e.target.dataset.key, -1);
          } else if (e.target.classList.contains('cart-item-remove')) {
            this.removeItem(e.target.dataset.key);
          }
        });
      }

      // Forms that go straight to checkout (data-direct-checkout) skip the
      // drawer, so a subscription there adds its gifts before leaving.
      document.addEventListener('submit', async (e) => {
        const form = e.target.closest('form[data-direct-checkout]');
        if (!form) return;
        const formData = new FormData(form);
        if (!formData.get('selling_plan') || !window.subscriptionGiftsActive || !(window.subscriptionGifts || []).length) return;
        e.preventDefault();
        const response = await fetch('/cart/add.js', { method: 'POST', body: formData }).catch(() => null);
        if (!response || !response.ok) { form.submit(); return; }
        await this.addSubscriptionGifts();
        window.location.href = '/checkout';
      });

      // Mobile add to cart
      const mobileAddToCart = document.getElementById('mobile-add-to-cart');
      if (mobileAddToCart) {
        mobileAddToCart.addEventListener('click', () => {
          const form = document.querySelector('form[action="/cart/add"]');
          if (form) this.addToCart(form);
        });
      }
    },

    async open() {
      if (this.cartDrawer) this.cartDrawer.classList.add('active');
      if (this.cartOverlay) this.cartOverlay.classList.add('active');
      lockScroll();
      
      // Track Cart Viewed
      try {
        const cart = await this.getCart();
        SegmentTracking.trackCartViewed(cart);
      } catch (error) {
        console.error('Error tracking cart viewed:', error);
      }
    },

    close() {
      if (this.cartDrawer) this.cartDrawer.classList.remove('active');
      if (this.cartOverlay) this.cartOverlay.classList.remove('active');
      unlockScroll();
    },

    async addToCart(form) {
      const formData = new FormData(form);

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          body: formData
        });

        if (!response.ok) {
          const err = await response.json().catch(() => ({}));
          this.showError(err.description || 'Could not add to cart. Please try again.');
          return;
        }

        const item = await response.json();
        const quantity = parseInt(formData.get('quantity')) || 1;
        SegmentTracking.trackProductAdded(item, quantity);
        if (formData.get('selling_plan')) await this.addSubscriptionGifts();
        await this.refresh();
        this.open();
      } catch (error) {
        console.error('Error adding to cart:', error);
        this.showError('Could not add to cart. Please try again.');
      }
    },

    async addToCartById(variantId, quantity = 1, source = null) {
      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: quantity })
        });

        if (!response.ok) {
          const err = await response.json().catch(() => ({}));
          this.showError(err.description || 'Could not add to cart. Please try again.');
          return;
        }

        const item = await response.json();
        SegmentTracking.trackProductAdded(item, quantity, source);
        await this.refresh();
        this.open();
      } catch (error) {
        console.error('Error adding to cart:', error);
        this.showError('Could not add to cart. Please try again.');
      }
    },

    async updateQuantity(key, delta) {
      try {
        const cart = await this.getCart();
        const item = cart.items.find(i => i.key === key);
        
        if (item) {
          const newQuantity = item.quantity + delta;
          
          if (newQuantity <= 0) {
            await this.removeItem(key);
          } else {
            await fetch('/cart/change.js', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                id: key,
                quantity: newQuantity
              })
            });
            
            await this.refresh();
          }
        }
      } catch (error) {
        console.error('Error updating quantity:', error);
      }
    },

    async removeItem(key) {
      try {
        // Get item info before removing for tracking
        const cart = await this.getCart();
        const item = cart.items.find(i => i.key === key);
        
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: key,
            quantity: 0
          })
        });
        
        // Track Product Removed
        if (item) {
          SegmentTracking.trackProductRemoved(item, item.quantity);
        }
        
        await this.refresh();
      } catch (error) {
        console.error('Error removing item:', error);
      }
    },

    async getCart() {
      const response = await fetch('/cart.js');
      if (!response.ok) throw new Error('Failed to fetch cart');
      return response.json();
    },

    showError(message) {
      const toast = document.createElement('div');
      toast.setAttribute('role', 'alert');
      toast.style.cssText = 'position:fixed;bottom:24px;left:50%;transform:translateX(-50%);background:#1C1917;color:#F5F0E8;padding:12px 24px;border-radius:8px;z-index:1000002;font-size:14px;max-width:90vw;text-align:center;';
      toast.textContent = message;
      document.body.appendChild(toast);
      setTimeout(() => { if (toast.parentNode) toast.remove(); }, 3500);
    },

    // Subscription gifts (snippets/subscription-gifts.liquid): one of each
    // gift product rides along with a subscription, marked with the hidden
    // _subscription_gift property plus a visible "Gift" line that checkout
    // and the order email show. The price drop comes from the automatic
    // discount in Shopify admin, not from here.
    // Two quick subscription adds would both see the gifts missing and add
    // them twice, so a second call waits on the one already running.
    addSubscriptionGifts() {
      if (!this._giftsInFlight) {
        this._giftsInFlight = this._addSubscriptionGifts().finally(() => { this._giftsInFlight = null; });
      }
      return this._giftsInFlight;
    },

    async _addSubscriptionGifts() {
      const giftIds = window.subscriptionGifts || [];
      if (!window.subscriptionGiftsActive || !giftIds.length) return;
      try {
        const cart = await this.getCart();
        const inCart = new Set(cart.items.filter(isSubscriptionGift).map(i => i.variant_id));
        const items = giftIds
          .filter(id => !inCart.has(id))
          .map(id => ({ id, quantity: 1, properties: { _subscription_gift: 'true', Gift: 'Free with your subscription' } }));
        if (!items.length) return;
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items })
        });
      } catch (error) {
        console.error('Error adding subscription gifts:', error);
      }
    },

    // Without a subscription in the cart, or once the offer has ended, the
    // discount no longer applies, so gifts left behind would be charged full
    // price: take them out.
    async syncSubscriptionGifts(cart) {
      const gifts = cart.items.filter(isSubscriptionGift);
      if (!gifts.length) return cart;
      if (window.subscriptionGiftsActive && cart.items.some(i => i.selling_plan_allocation)) return cart;
      const updates = {};
      gifts.forEach(i => { updates[i.key] = 0; });
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates })
      });
      return response.ok ? response.json() : cart;
    },

    async refresh() {
      const cart = await this.syncSubscriptionGifts(await this.getCart());
      this.updateUI(cart);
    },

    updateUI(cart) {
      // Update cart count in header
      if (this.headerCartCount) {
        this.headerCartCount.textContent = cart.item_count;
      }
      // Also update any elements with data-cart-count
      document.querySelectorAll('[data-cart-count]').forEach(el => {
        el.textContent = cart.item_count;
      });

      // Update cart total
      if (this.cartTotal) {
        this.cartTotal.textContent = formatMoney(cart.total_price);
      }

      // Update cart items
      // Cache for use by the checkout tracking handler in cart-drawer.liquid
      window._cartData = cart;

      if (this.cartItems) {
        if (cart.item_count === 0) {
          this.cartItems.innerHTML = '<div class="cart-empty"><p>Your cart is empty</p><a href="/collections/all" class="btn-continue-shopping">Continue Shopping</a></div>';
        } else {
          // Build cart item DOM nodes without innerHTML interpolation of API
          // data to prevent XSS if a product title/variant contains HTML.
          const fragment = document.createDocumentFragment();
          cart.items.forEach(item => {
            const el = document.createElement('div');
            el.className = 'cart-item';
            el.dataset.lineItemKey = item.key;

            const imgHtml = item.image
              ? `<img src="${sanitizeText(item.image.replace(/(\.[^.]+)$/, '_128x128$1'))}" alt="" loading="lazy">`
              : '';

            el.innerHTML = `
              <div class="cart-item-image">${imgHtml}</div>
              <div class="cart-item-details">
                <h4></h4>
                <div class="price"></div>
                <div class="quantity-controls">
                  <button class="qty-decrease" data-key="" aria-label="Decrease quantity">-</button>
                  <span></span>
                  <button class="qty-increase" data-key="" aria-label="Increase quantity">+</button>
                </div>
              </div>
              <button class="cart-item-remove" data-key="" aria-label="Remove item">&times;</button>
            `;

            el.querySelector('h4').textContent = item.product_title || '';
            el.querySelector('.quantity-controls span').textContent = item.quantity;
            el.querySelector('.price').textContent = formatMoney(item.final_line_price / item.quantity);
            // Struck-through price: the product's compare-at price when it is
            // higher (window.cartCompareAt, from the cart drawer snippet),
            // otherwise the pre-discount price.
            const compareAt = (window.cartCompareAt || {})[item.variant_id] || 0;
            const unitPrice = item.final_line_price / item.quantity;
            const wasPrice = Math.max(compareAt, item.original_price);
            if (wasPrice > unitPrice) {
              const compareEl = document.createElement('s');
              compareEl.className = 'price-compare';
              compareEl.textContent = formatMoney(wasPrice);
              el.querySelector('.price').prepend(compareEl, ' ');
            }
            if (isSubscriptionGift(item)) {
              const giftEl = document.createElement('div');
              giftEl.className = 'variant';
              giftEl.textContent = 'Free with your subscription';
              el.querySelector('.cart-item-details').insertBefore(giftEl, el.querySelector('.price'));
              el.querySelector('.quantity-controls').remove();
            }
            el.querySelectorAll('[data-key]').forEach(node => { node.dataset.key = item.key; });

            if (item.variant_title && item.variant_title !== 'Default Title') {
              const variantEl = document.createElement('div');
              variantEl.className = 'variant';
              variantEl.textContent = item.variant_title;
              el.querySelector('.cart-item-details').insertBefore(variantEl, el.querySelector('.price'));
            }

            fragment.appendChild(el);
          });

          this.cartItems.innerHTML = '';
          this.cartItems.appendChild(fragment);
        }
      }

      this.updateUpsell(cart);
    },

    // Cart upsell (snippets/cart-drawer.liquid): show it when the cart has
    // items and does not already hold the upsell product.
    updateUpsell(cart) {
      const upsell = document.getElementById('cart-upsell');
      if (!upsell) return;
      const ids = (upsell.dataset.variantIds || '').split(',').filter(Boolean).map(Number);
      const inCart = cart.items.some(item => ids.includes(item.variant_id));
      upsell.hidden = cart.item_count === 0 || inCart;
    }
  };

  // ==========================================
  // Product Gallery
  // ==========================================
  
  const ProductGallery = {
    init() {
      const thumbnails = document.querySelectorAll('.thumbnail');
      const mainImage = document.getElementById('gallery-main-img');

      if (!thumbnails.length || !mainImage) return;

      thumbnails.forEach(thumb => {
        thumb.addEventListener('click', () => {
          // Update main image
          const imageUrl = thumb.dataset.imageUrl;
          mainImage.src = imageUrl;

          // Update active state
          thumbnails.forEach(t => t.classList.remove('active'));
          thumb.classList.add('active');
        });
      });
    }
  };

  // ==========================================
  // Quantity Selector
  // ==========================================
  
  const QuantitySelector = {
    init() {
      document.querySelectorAll('.qty-selector').forEach(selector => {
        const decreaseBtn = selector.querySelector('[data-action="decrease"], .qty-decrease');
        const increaseBtn = selector.querySelector('[data-action="increase"], .qty-increase');
        const input = selector.querySelector('.qty-value');

        if (!decreaseBtn || !increaseBtn || !input) return;

        decreaseBtn.addEventListener('click', () => {
          const currentValue = parseInt(input.value) || 1;
          if (currentValue > 1) {
            input.value = currentValue - 1;
            this.updatePrice(input);
          }
        });

        increaseBtn.addEventListener('click', () => {
          const currentValue = parseInt(input.value) || 1;
          const max = parseInt(input.max) || 10;
          if (currentValue < max) {
            input.value = currentValue + 1;
            this.updatePrice(input);
          }
        });
      });
    },

    updatePrice(input) {
      // Scope to the passed input to handle multiple selectors on one page
      const priceSpan = document.querySelector('.add-to-cart-price');
      if (priceSpan && input && window.productPrice) {
        const qty = parseInt(input.value) || 1;
        priceSpan.textContent = formatMoney(window.productPrice * qty);
      }
    }
  };

  // ==========================================
  // Pricing Options
  // ==========================================
  
  const PricingOptions = {
    init() {
      const options = document.querySelectorAll('.pricing-option');
      const sellingPlanInput = document.getElementById('selling-plan-input');

      if (!options.length) return;

      options.forEach(option => {
        option.addEventListener('click', () => {
          // Update selection
          options.forEach(o => o.classList.remove('selected'));
          option.classList.add('selected');

          // Update hidden input
          if (sellingPlanInput) {
            sellingPlanInput.value = option.dataset.sellingPlanId || '';
          }
        });
      });
    }
  };

  // ==========================================
  // FAQ Accordion
  // ==========================================
  
  const FAQ = {
    init() {
      const faqItems = document.querySelectorAll('.faq-item');

      faqItems.forEach(item => {
        const question = item.querySelector('.faq-question');
        
        if (question) {
          question.addEventListener('click', () => {
            const isOpen = item.classList.contains('open');
            
            // Close all other items
            faqItems.forEach(i => i.classList.remove('open'));
            
            // Toggle current item
            if (!isOpen) {
              item.classList.add('open');
            }
          });

          // Keyboard accessibility
          question.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              question.click();
            }
          });
        }
      });
    }
  };

  // ==========================================
  // Testimonials Filter
  // ==========================================
  
  const TestimonialsFilter = {
    init() {
      const tabs = document.querySelectorAll('.filter-tab');
      const cards = document.querySelectorAll('.testimonial-card');

      if (!tabs.length) return;

      tabs.forEach(tab => {
        tab.addEventListener('click', () => {
          const filter = tab.dataset.filter;

          // Update active tab
          tabs.forEach(t => t.classList.remove('active'));
          tab.classList.add('active');

          // Filter cards
          cards.forEach(card => {
            if (filter === 'all' || card.dataset.product === filter) {
              card.style.display = '';
            } else {
              card.style.display = 'none';
            }
          });
        });
      });
    }
  };

  // ==========================================
  // Mobile Menu
  // ==========================================
  
  const MobileMenu = {
    init() {
      this.toggle = document.getElementById('mobile-menu-toggle');
      this.nav = document.getElementById('mobile-nav');

      if (!this.toggle || !this.nav) return;

      this.toggle.addEventListener('click', () => {
        this.nav.classList.toggle('active');
        this.toggle.classList.toggle('active');
      });
    }
  };

  // ==========================================
  // Announcement Bar
  // ==========================================
  
  const AnnouncementBar = {
    init() {
      const closeBtn = document.querySelector('.announcement-close');
      const bar = document.querySelector('.announcement-bar');

      if (!closeBtn || !bar) return;

      closeBtn.addEventListener('click', () => {
        bar.style.display = 'none';
        sessionStorage.setItem('announcementClosed', 'true');
      });

      // Check if already closed
      if (sessionStorage.getItem('announcementClosed') === 'true') {
        bar.style.display = 'none';
      }
    }
  };

  // ==========================================
  // Video Player
  // ==========================================
  
  const VideoPlayer = {
    init() {
      const wrappers = document.querySelectorAll('.video-wrapper');

      wrappers.forEach(wrapper => {
        const playBtn = wrapper.querySelector('.play-button');
        if (!playBtn) return;

        playBtn.addEventListener('click', () => {
          const videoId = wrapper.dataset.videoId;
          const videoType = wrapper.dataset.videoType || 'youtube';

          // Validate videoId to alphanumeric + hyphens/underscores only
          // before using it in a URL to prevent injection via data attributes.
          const safeId = /^[a-zA-Z0-9_-]{1,64}$/.test(videoId) ? videoId : null;
          if (videoType === 'youtube' && safeId) {
            const iframe = document.createElement('iframe');
            iframe.width = '100%';
            iframe.height = '100%';
            iframe.src = `https://www.youtube.com/embed/${safeId}?autoplay=1`;
            iframe.setAttribute('frameborder', '0');
            iframe.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture');
            iframe.setAttribute('allowfullscreen', '');
            wrapper.innerHTML = '';
            wrapper.appendChild(iframe);
          } else if (videoType === 'vimeo' && safeId) {
            const iframe = document.createElement('iframe');
            iframe.width = '100%';
            iframe.height = '100%';
            iframe.src = `https://player.vimeo.com/video/${safeId}?autoplay=1`;
            iframe.setAttribute('frameborder', '0');
            iframe.setAttribute('allow', 'autoplay; fullscreen');
            iframe.setAttribute('allowfullscreen', '');
            wrapper.innerHTML = '';
            wrapper.appendChild(iframe);
          }
        });
      });
    }
  };

  // ==========================================
  // Smooth Scroll
  // ==========================================
  
  const SmoothScroll = {
    init() {
      document.querySelectorAll('a[href^="#"]').forEach(anchor => {
        anchor.addEventListener('click', function(e) {
          const href = this.getAttribute('href');
          if (href === '#') return;

          const target = document.querySelector(href);
          if (target) {
            e.preventDefault();
            target.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          }
        });
      });
    }
  };

  // ==========================================
  // Quiz Modal - Find Your State (7-question rebuild)
  // Per docs/plans/2026-07-22_quiz-revamp-prd.md §5.2/§5.3/§9.
  // Replaces the old 10-question engine (quizQuestions10, startQuiz10,
  // renderQuizQuestions, calculateQuizResults, resetQuiz10, etc.) per §8.
  // ==========================================

  // ---- Quiz state -------------------------------------------------------
  const quizState = {
    current: 0,        // 0 = intro, 1-7 = questions, 8 = email step or result
    answers: [],        // answers[qIdx] = { text, states, weight, index }
    started: false,
    completed: false,
    source: 'unknown',
    result: null         // { topMatch, pairsWith, scores } once computed
  };

  let quizTriggerEl = null;         // element to restore focus to on close
  let quizFocusTrapHandler = null;  // bound keydown handler while open

  function pushDataLayer(event, payload) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({ event: event }, payload || {}));
  }

  // Shopify customer events: a custom pixel (Settings > Customer events)
  // subscribes to these and forwards them to Meta for quiz audiences.
  function quizPublish(event, payload) {
    try {
      if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') {
        Shopify.analytics.publish(event, payload || {});
      }
    } catch (e) { /* analytics must never break the quiz */ }
  }

  function quizAnswerSummary(result) {
    const a = quizState.answers;
    const first = (id) => { const x = a.filter((y) => y && y.id === id)[0]; return x && x.states && x.states[0] ? x.states[0] : null; };
    return {
      top_match: result.topMatch,
      pairs_with: result.pairsWith,
      concern: first('concern'),
      outcome: first('outcome'),
      history: quizAnswerKey(a, 'history'),
      start: quizAnswerKey(a, 'start'),
      proof: quizAnswerKey(a, 'proof'),
      source: quizState.source
    };
  }

  // ---- Question data ----------------------------------------------------
  // Seven questions in the order of the Seed-style funnel: main problem, when
  // it shows up, desired outcome, current habit (scored), then history,
  // commitment and proof (unscored: `key` picks the result-page angle and
  // becomes a customer tag for Braze). Weights: Q1 3, Q2 2, Q3 2, Q4 1.
  // Dual-state answers give the full weight to EACH listed state.
  const QUIZ_QUESTIONS = [
    {
      id: 'concern',
      eyebrow: 'FIND YOUR OPTIMAL STATE',
      question: "What's the one thing you'd most love to change right now?",
      helper: 'Your #1 goal carries the most weight.',
      weight: 3,
      options: [
        { text: "I can't switch off — I'm always wired or on edge", states: ['zen'] },
        { text: "I can't focus — I get distracted and lose momentum", states: ['focus'] },
        { text: "My mood's been flat, or I feel disconnected from people", states: ['bliss'] },
        { text: "I sleep badly, or wake up tired no matter what", states: ['dream'] },
        { text: "My physical energy and drive have dropped off", states: ['gazm'] }
      ]
    },
    {
      id: 'when',
      eyebrow: 'FIND YOUR OPTIMAL STATE',
      question: 'When does your day usually start to slip?',
      helper: null,
      weight: 2,
      options: [
        { text: 'Mornings — hard to get going and locked in', states: ['focus'] },
        { text: 'Midday — stress and pressure pile up', states: ['zen'] },
        { text: 'Evenings — I struggle to feel social or present', states: ['bliss'] },
        { text: 'Night — I lie awake or sleep poorly', states: ['dream'] },
        { text: 'Workouts and physical moments feel flat', states: ['gazm'] }
      ]
    },
    {
      id: 'outcome',
      eyebrow: 'FIND YOUR OPTIMAL STATE',
      question: '30 days from now, what would make you say "this worked"?',
      helper: 'Pick the one that matters most.',
      weight: 2,
      options: [
        { text: 'I get through deep work without losing momentum', states: ['focus'] },
        { text: 'I stay steady when the pressure is on', states: ['zen'] },
        { text: 'I feel like myself around people again', states: ['bliss'] },
        { text: 'I wake up rested most mornings', states: ['dream'] },
        { text: 'I have energy for training and the rest of my day', states: ['gazm'] }
      ]
    },
    {
      id: 'coffee',
      eyebrow: 'FIND YOUR OPTIMAL STATE',
      question: "How's your relationship with coffee?",
      helper: "There's no wrong answer — States works with it, not against it.",
      weight: 1,
      options: [
        { text: 'I run on it, and the crashes are real', states: ['focus'] },
        { text: 'It makes me jittery and anxious', states: ['zen', 'focus'] },
        { text: 'I drink it late and it wrecks my sleep', states: ['dream'] },
        { text: "We're fine — that's honestly not my issue", states: [] }
      ]
    },
    {
      id: 'history',
      eyebrow: 'ALMOST THERE',
      question: 'Have you tried supplements for this before?',
      helper: null,
      weight: 0,
      options: [
        { text: "Yes, and I didn't feel much", states: [], key: 'skeptic' },
        { text: 'Yes, and some helped a little', states: [], key: 'tried' },
        { text: 'No, this would be my first', states: [], key: 'new' }
      ]
    },
    {
      id: 'start',
      eyebrow: 'ALMOST THERE',
      question: 'How do you want to start?',
      helper: null,
      weight: 0,
      options: [
        { text: 'As a daily routine I can build on', states: [], key: 'daily' },
        { text: 'Just for the moments that matter', states: [], key: 'moments' },
        { text: 'Try it first, with the 60-day money-back guarantee', states: [], key: 'trial' }
      ]
    },
    {
      id: 'proof',
      eyebrow: 'LAST QUESTION',
      question: 'What would make you say yes?',
      helper: null,
      weight: 0,
      options: [
        { text: 'Seeing every ingredient and dose', states: [], key: 'ingredients' },
        { text: 'Hearing from people who take it', states: [], key: 'reviews' },
        { text: 'Knowing who formulated it', states: [], key: 'formulator' },
        { text: 'A money-back guarantee', states: [], key: 'guarantee' }
      ]
    }
  ];

  // ---- Result-card product data (§5.3 + orchestrator contract) ----------
  // FOCUS/DREAM accent + tagline + ingredients are verbatim from the Figma
  // extract (§5.2). ZEN/BLISS/GAZM taglines/ingredients aren't in the Figma
  // extract — PLACEHOLDER copy below, needs marketing sign-off (flagged
  // exactly like the PRD flagged its own placeholders before §12 resolved
  // them). Accent colors for ZEN/BLISS/GAZM use the general brand tokens
  // (var(--color-zen/bliss/gazm)) per orchestrator contract, NOT the Figma
  // literal hexes (which only exist for FOCUS/DREAM).
  // Ingredients mirror each product template's product-ingredients section
  // (the earlier ZEN/BLISS/GAZM placeholders named ingredients the products
  // don't contain). Keep them in sync when a formula changes.
  const QUIZ_PRODUCTS = {
    focus: {
      name: 'FOCUS',
      accent: '#005CFF',
      timeOfDay: 'MORNING',
      tagline: 'Four hours of deep, unbroken focus — with or without your coffee.',
      ingredients: 'enfinity® paraxanthine 300mg + PEAK ATP® 400mg + L-Tyrosine 500mg',
      capsules: 4,
      howToTake: '4 capsules in the morning, 30–60 minutes before deep work.',
      echoSuffix: 'FOCUS is built for exactly that — clean, sustained concentration with no jitters and no crash.'
    },
    dream: {
      name: 'DREAM',
      accent: '#7ABFFF',
      timeOfDay: 'NIGHT',
      tagline: 'Fall asleep easier and wake up genuinely rested — no grogginess, no morning fog.',
      ingredients: 'EZGABA® 750mg + Magnesium glycinate 85mg + Mugwort 450mg',
      capsules: 4,
      howToTake: '4 capsules, 30–60 minutes before bed.',
      echoSuffix: 'DREAM targets that directly — easier, deeper, natural sleep, so you wake up genuinely rested.'
    },
    zen: {
      name: 'ZEN',
      accent: 'var(--color-zen)',
      timeOfDay: 'MIDDAY',
      // PLACEHOLDER — needs marketing sign-off (not in Figma extract).
      tagline: 'Steady calm under pressure — without the drowsy crash.',
      ingredients: 'Lemon balm 500mg + L-Theanine 200mg + RhodioPrime® 6X 150mg',
      capsules: 2,
      howToTake: '2 capsules in the morning or early afternoon. 4 before a high-pressure moment.',
      echoSuffix: 'ZEN is built for exactly that — steady calm without feeling flat or foggy.'
    },
    bliss: {
      name: 'BLISS',
      accent: 'var(--color-bliss)',
      timeOfDay: 'EVENING',
      // PLACEHOLDER — needs marketing sign-off (not in Figma extract).
      tagline: 'A genuine lift in mood and connection — no drink required.',
      ingredients: 'Affron® saffron 28mg + Zembrin® 25mg + CurcuPrime® 250mg',
      capsules: 2,
      howToTake: '2 capsules in the morning, or 30–60 minutes before you go out.',
      echoSuffix: 'BLISS is built for exactly that — a genuine lift in mood and connection, no crash after.'
    },
    gazm: {
      name: 'GAZM',
      accent: 'var(--color-gazm)',
      timeOfDay: 'ANYTIME',
      // PLACEHOLDER — needs marketing sign-off (not in Figma extract).
      tagline: 'Energy and drive that show up, in workouts and in life.',
      ingredients: 'Nitrosigine® 1.5g + Tongkat Ali 200mg + Shoden® ashwagandha 120mg',
      capsules: 4,
      howToTake: '4 capsules, 60–90 minutes before training.',
      echoSuffix: 'GAZM is built for exactly that — real physical energy and drive, not just a stimulant buzz.'
    }
  };

  // ---- Stack naming (§5.3 — verbatim, final for all 10 pairs) -----------
  // Keyed by the two handles sorted alphabetically so lookup doesn't care
  // which one is top-match vs pairs-with.
  const QUIZ_STACKS = {
    'dream+focus': { name: 'The Deep Work Stack', line: 'Sharp, productive days built on real, restorative recovery.' },
    // PLACEHOLDER stack lines below — PRD §5.3 only gave names for these 4
    // pairs, no stack-line copy; needs marketing sign-off before launch.
    'focus+zen': { name: 'The Calm Clarity Stack', line: 'FOCUS + ZEN — built to work together.' },
    'dream+zen': { name: 'The Unwind Stack', line: 'ZEN + DREAM — built to work together.' },
    'bliss+dream': { name: 'The Social Reset Stack', line: 'BLISS + DREAM — built to work together.' },
    'focus+gazm': { name: 'The Performance Stack', line: 'GAZM + FOCUS — built to work together.' },
    'bliss+focus': { name: 'The Bright Focus Stack', line: 'Clear-headed drive paired with a genuine lift in mood.' },
    'bliss+zen': { name: 'The Ease Stack', line: 'Steady calm and a warmer, more connected mood.' },
    'gazm+zen': { name: 'The Steady Drive Stack', line: "Calm under pressure, with energy that doesn't quit." },
    'bliss+gazm': { name: 'The Full Charge Stack', line: 'Feel-good energy for the whole day.' },
    'dream+gazm': { name: 'The Recovery Stack', line: 'Real rest at night, real drive during the day.' }
  };

  // Fixed complement fallback (§5.3 point 2) when all remaining scores are 0.
  const QUIZ_COMPLEMENT = { focus: 'dream', dream: 'focus', zen: 'bliss', bliss: 'zen', gazm: 'focus' };

  const QUIZ_STATE_KEYS = ['focus', 'zen', 'bliss', 'dream', 'gazm'];

  function quizStackInfo(a, b) {
    const key = [a, b].sort().join('+');
    return QUIZ_STACKS[key] || { name: 'Your Stack', line: '' };
  }

  // ---- Scoring / result selection (§5.3) --------------------------------
  function quizComputeResult(answers) {
    const running = { focus: 0, zen: 0, bliss: 0, dream: 0, gazm: 0 };
    const cumulative = [];

    answers.forEach((a) => {
      if (a && a.states && a.states.length) {
        a.states.forEach((s) => { running[s] += a.weight; });
      }
      cumulative.push(Object.assign({}, running));
    });

    const scores = running;

    // Among tied states for a given total, find whichever state's cumulative
    // score FIRST reached that total (earliest question) — deterministic
    // tie-break per §5.3 point 1.
    function earliestReachIndex(state, total) {
      for (let i = 0; i < cumulative.length; i++) {
        if (cumulative[i][state] === total) return i;
      }
      return cumulative.length;
    }

    function pickWinner(pool) {
      if (!pool.length) return null;
      const maxScore = Math.max.apply(null, pool.map((s) => scores[s]));
      const tied = pool.filter((s) => scores[s] === maxScore);
      if (tied.length === 1) return tied[0];

      // Q1's mapped state wins if it's among the tied states.
      const q1 = answers[0];
      if (q1 && q1.states && q1.states.length) {
        const q1State = q1.states.filter((s) => tied.indexOf(s) !== -1)[0];
        if (q1State) return q1State;
      }

      // Otherwise: earliest question to reach the tied total wins.
      tied.sort((a, b) => earliestReachIndex(a, scores[a]) - earliestReachIndex(b, scores[b]));
      return tied[0];
    }

    const topMatch = pickWinner(QUIZ_STATE_KEYS.slice());
    let pairsWith = pickWinner(QUIZ_STATE_KEYS.filter((s) => s !== topMatch));

    // §5.3 point 2: if all remaining states are 0, fall back to the fixed
    // complement of the top match.
    if (pairsWith && scores[pairsWith] === 0) {
      pairsWith = QUIZ_COMPLEMENT[topMatch];
    }

    return { topMatch: topMatch, pairsWith: pairsWith, scores: scores };
  }

  // Personalized echo: strongest-weighted answer mapping to `state` — Q1
  // preferred, else highest-weight contributor (§5.3). Ties fall back to
  // question order (`answers` is already Q1→Q7, and `forEach` preserves
  // that order, so `>` — not `>=` — keeps the earliest-asked tied answer).
  function quizFindEchoAnswer(state, answers) {
    const q1 = answers[0];
    if (q1 && q1.states && q1.states.indexOf(state) !== -1) return q1.text;

    let best = null;
    answers.forEach((a) => {
      if (!a || !a.states || a.states.indexOf(state) === -1) return;
      if (!best || a.weight > best.weight) best = a;
    });
    return best ? best.text : null;
  }

  function quizFormatPrice(n) {
    const num = Number(n);
    return Number.isInteger(num) ? String(num) : num.toFixed(2);
  }

  // Inline capsule icon — no capsule/serving asset exists in assets/, so this
  // is rendered as a small inline SVG rather than inventing a binary asset.
  function quizCapsuleIconSvg() {
    return '<svg class="quiz-capsule-icon" width="16" height="7" viewBox="0 0 16 7" fill="none" aria-hidden="true" focusable="false">'
      + '<rect x="0.5" y="0.5" width="15" height="6" rx="3" fill="currentColor"/>'
      + '</svg>';
  }

  // ---- Result card / screen rendering -----------------------------------
  function quizBuildCardHtml(handle, answers, cardId, badgeLabel) {
    const info = QUIZ_PRODUCTS[handle];
    const price = (window.quizProductPrices && window.quizProductPrices[handle]) || 59;
    const comparePrice = (window.quizProductComparePrices && window.quizProductComparePrices[handle]) || 99;
    const img = (window.quizResultImages && window.quizResultImages[handle]) || '';
    const answerText = quizFindEchoAnswer(handle, answers);

    const echoHtml = answerText
      ? 'You told us <strong>“' + sanitizeText(answerText) + '”</strong>. ' + sanitizeText(info.echoSuffix)
      : sanitizeText(info.tagline);

    const servingIcons = quizCapsuleIconSvg().repeat(info.capsules || 4);

    return ''
      + '<div class="quiz-result-card" id="' + cardId + '">'
      + (img ? '<img class="quiz-result-card__photo" src="' + img + '" alt="' + sanitizeText(info.name) + '" loading="lazy" width="303" height="264">' : '')
      + '<div class="quiz-result-card__body">'
      + '<span class="quiz-result-card__badge">' + sanitizeText(badgeLabel) + '</span>'
      + '<h3 class="quiz-result-card__name"><span style="color:' + info.accent + '">' + sanitizeText(info.name) + '</span> &rarr; ' + sanitizeText(info.timeOfDay) + '</h3>'
      + '<p class="quiz-result-card__tagline">' + sanitizeText(info.tagline) + '*</p>'
      + '<div class="quiz-result-card__serving">' + servingIcons + '<span class="quiz-result-card__serving-text">(1 serving)</span></div>'
      + '<p class="quiz-result-card__echo">' + echoHtml + '</p>'
      + '<p class="quiz-result-card__how"><strong>How to take it:</strong> ' + sanitizeText(info.howToTake) + '</p>'
      + '<p class="quiz-result-card__ingredients">' + sanitizeText(info.ingredients) + '</p>'
      + '<div class="quiz-result-card__price-row">'
      + '<span class="quiz-result-card__price">$' + quizFormatPrice(price) + '</span>'
      + '<span class="quiz-result-card__compare-price">$' + Number(comparePrice).toFixed(2) + '</span>'
      + '</div>'
      + '<button type="button" class="quiz-result-atc" data-handle="' + handle + '">Add to Cart</button>'
      + '</div>'
      + '</div>';
  }

  function quizAnswerKey(answers, id) {
    const a = answers.filter((x) => x && x.id === id)[0];
    return a ? a.key : null;
  }

  // "Why this fits you": every scored answer that pointed at the top match,
  // then one angle line from the unscored history / start / proof answers.
  function quizBuildWhyHtml(topMatch, answers) {
    const info = QUIZ_PRODUCTS[topMatch];
    const said = answers
      .filter((a) => a && a.weight > 0 && a.states && a.states.indexOf(topMatch) !== -1)
      .slice(0, 3)
      .map((a) => '<li>You said <strong>“' + sanitizeText(a.text) + '”</strong></li>')
      .join('');

    // One angle line, in priority order: a skeptic first gets reassurance,
    // then what they said would convince them, then how they want to start.
    const guarantee = '60-day money-back guarantee. If you don’t notice a real difference, you get your money back.';
    const history = quizAnswerKey(answers, 'history');
    const proof = quizAnswerKey(answers, 'proof');
    const start = quizAnswerKey(answers, 'start');
    let angle = '';
    let reviewsLink = '';
    if (history === 'skeptic') {
      angle = 'You’ve tried supplements before and didn’t feel much. Every dose in ' + info.name + ' is on the label, at the amount it’s meant to be taken, with a ' + guarantee;
    } else if (proof === 'ingredients') {
      angle = 'What’s inside ' + info.name + ': ' + info.ingredients + '. No proprietary blends.';
    } else if (proof === 'formulator') {
      angle = 'Formulated by Shawn Wells, MPH, RD.';
    } else if (proof === 'reviews') {
      reviewsLink = '<a class="quiz-why__link" href="/products/' + topMatch + '">Read what people say about ' + sanitizeText(info.name) + ' &rarr;</a>';
    } else if (proof === 'guarantee' || start === 'trial') {
      angle = guarantee;
    } else if (start === 'moments') {
      angle = 'Keep it for the moments that matter: ' + info.howToTake;
    }
    const lines = angle ? [angle] : [];

    if (!said && !lines.length && !reviewsLink) return '';
    return ''
      + '<div class="quiz-why">'
      + '<h3 class="quiz-why__heading">Why ' + sanitizeText(info.name) + ' fits you</h3>'
      + (said ? '<ul class="quiz-why__said">' + said + '</ul>' : '')
      + lines.map((l) => '<p class="quiz-why__line">' + sanitizeText(l) + '</p>').join('')
      + reviewsLink
      + '</div>';
  }

  // Black Friday Box Set box. Hidden unless window.quizBoxSetPromo is set:
  // the Black Friday header (PR #331) sets it while the Box Set is in stock,
  // so the quiz offer goes live and sells out with the rest of the site.
  function quizBuildPromoHtml(topMatch, pairsWith) {
    const promo = window.quizBoxSetPromo;
    if (!promo || !window.quizProductIds || !window.quizProductIds['box-set']) return '';
    const price = window.quizProductPrices && window.quizProductPrices['box-set'];
    const compare = window.boxSetComparePrice;
    return ''
      + '<div class="quiz-bf">'
      + '<span class="quiz-bf__badge">' + sanitizeText(promo.badge || 'Black Friday · 50% off') + '</span>'
      + '<h3 class="quiz-bf__heading">' + sanitizeText(promo.heading || 'Get all five States in the Box Set') + '</h3>'
      + '<p class="quiz-bf__copy">Your match is ' + sanitizeText(QUIZ_PRODUCTS[topMatch].name) + ' + ' + sanitizeText(QUIZ_PRODUCTS[pairsWith].name)
      + '. The Box Set has both, plus the other three States, ' + sanitizeText(promo.copy || 'at 50% off for Black Friday.') + '</p>'
      + (price ? '<div class="quiz-result-card__price-row"><span class="quiz-result-card__price">$' + quizFormatPrice(price) + '</span>'
        + (compare && compare > price ? '<span class="quiz-result-card__compare-price">$' + Number(compare).toFixed(2) + '</span>' : '') + '</div>' : '')
      + '<button type="button" class="quiz-btn-shop-stack" id="quiz-boxset-btn">' + sanitizeText(promo.button || 'Add the Box Set') + '</button>'
      + '</div>';
  }

  // One offer per screen. While the Black Friday Box Set box shows, the
  // two-bottle stack drops to a quiet link so it doesn't compete with it.
  // ("Any 3, save 30%" is no longer offered in the quiz.)
  function quizBuildStackHtml(topInfo, pairsInfo, promoActive) {
    const names = sanitizeText(topInfo.name) + ' + ' + sanitizeText(pairsInfo.name);
    if (promoActive) {
      return '<button type="button" class="quiz-retake-link quiz-stack-link" id="quiz-shop-stack-btn">Just get ' + names + ' &rarr;</button>';
    }
    return ''
      + '<div class="quiz-result-upsell">'
      + '<h3 class="quiz-result-upsell__heading">Start with your stack</h3>'
      + '<p class="quiz-result-upsell__copy">Add ' + names + ' together.</p>'
      + '<div class="quiz-result-upsell__actions">'
      + '<button type="button" class="quiz-btn-shop-stack" id="quiz-shop-stack-btn">Shop your stack &rarr;</button>'
      + '</div>'
      + '</div>';
  }

  function quizBuildResultHtml(topMatch, pairsWith, answers) {
    const stack = quizStackInfo(topMatch, pairsWith);
    const topInfo = QUIZ_PRODUCTS[topMatch];
    const pairsInfo = QUIZ_PRODUCTS[pairsWith];
    const promo = quizBuildPromoHtml(topMatch, pairsWith);

    return ''
      + '<h2 class="quiz-result-heading">Your top two states</h2>'
      + '<h3 class="quiz-result-stack-name">' + sanitizeText(stack.name) + '</h3>'
      + (stack.line ? '<p class="quiz-result-stack-line">' + sanitizeText(stack.line) + '*</p>' : '')
      + '<div class="quiz-result-cards">'
      + quizBuildCardHtml(topMatch, answers, 'quiz-result-top-match', 'TOP MATCH')
      + quizBuildCardHtml(pairsWith, answers, 'quiz-result-pairs-with', 'PAIRS WITH')
      + '</div>'
      + quizBuildWhyHtml(topMatch, answers)
      + promo
      + quizBuildStackHtml(topInfo, pairsInfo, !!promo)
      + '<div class="quiz-result-footer">'
      + '<button type="button" class="quiz-retake-link" id="quiz-retake-btn">&#8635; Retake the quiz</button>'
      + '<hr class="quiz-result-divider">'
      + '<p class="quiz-fda-disclaimer"><strong>*These statements have not been evaluated by the Food and Drug Administration. This product is not intended to diagnose, treat, cure, or prevent any disease.</strong> If you are taking medication or under a physician\'s care, consult your healthcare professional before use.</p>'
      + '</div>';
  }

  // ---- Progress bar ------------------------------------------------------
  function quizUpdateProgress(n, opts) {
    opts = opts || {};
    const segments = document.querySelectorAll('#quiz-progress .quiz-progress-segment');
    segments.forEach((seg) => {
      const step = parseInt(seg.getAttribute('data-step'), 10);
      seg.classList.toggle('is-filled', opts.allFilled || step <= n);
    });
    const counter = document.getElementById('quiz-progress-counter');
    if (counter) counter.textContent = opts.counterText || (n + ' / 7');
    const eyebrow = document.getElementById('quiz-eyebrow');
    if (eyebrow) eyebrow.textContent = opts.eyebrowText || 'FIND YOUR OPTIMAL STATE';
    const sr = document.getElementById('quiz-progress-sr');
    if (sr) sr.textContent = opts.srText || ('Step ' + n + ' of 7');
  }

  // ---- Question navigation ----------------------------------------------
  function quizRenderQuestions() {
    const container = document.getElementById('quiz-questions-container');
    if (!container) return;

    container.innerHTML = QUIZ_QUESTIONS.map((q, idx) => {
      const n = idx + 1;
      const optionsHtml = q.options.map((opt, i) => ''
        + '<div class="quiz-answer-card">'
        + '<input type="radio" class="quiz-answer-input" name="quiz-q' + n + '" id="quiz-q' + n + '-opt' + i + '" value="' + i + '">'
        + '<label class="quiz-answer-label" for="quiz-q' + n + '-opt' + i + '">' + sanitizeText(opt.text) + '</label>'
        + '</div>'
      ).join('');

      return ''
        + '<div class="quiz-question-screen" id="quiz-q' + n + '"' + (n === 1 ? '' : ' hidden') + '>'
        + '<fieldset class="quiz-fieldset">'
        + '<legend class="quiz-question-legend"><h2 class="quiz-question-heading">' + sanitizeText(q.question) + '</h2></legend>'
        + (q.helper ? '<p class="quiz-helper-text">' + sanitizeText(q.helper) + '</p>' : '')
        + '<div class="quiz-answer-list">' + optionsHtml + '</div>'
        + '</fieldset>'
        + '</div>';
    }).join('');
  }

  function quizSyncQuestionSelection(n) {
    const stored = quizState.answers[n - 1];
    const screen = document.getElementById('quiz-q' + n);
    const nextBtn = document.getElementById('quiz-next-btn');
    if (screen) {
      const radios = screen.querySelectorAll('input[type="radio"]');
      radios.forEach((r, i) => {
        const isChecked = !!stored && stored.index === i;
        r.checked = isChecked;
        const card = r.closest('.quiz-answer-card');
        if (card) card.classList.toggle('is-selected', isChecked);
      });
    }
    if (nextBtn) nextBtn.disabled = !stored;
  }

  function quizShowQuestion(n) {
    quizState.current = n;
    for (let i = 1; i <= 7; i++) {
      const el = document.getElementById('quiz-q' + i);
      if (el) el.hidden = (i !== n);
    }
    const backBtn = document.getElementById('quiz-back-btn');
    // No Back button on Q1 at all (PRD §12 resolution #2).
    if (backBtn) backBtn.hidden = (n === 1);

    quizUpdateProgress(n, { eyebrowText: QUIZ_QUESTIONS[n - 1].eyebrow });
    quizSyncQuestionSelection(n);
  }

  function quizHandleNext() {
    const n = quizState.current;
    if (n < 1 || n > 7) return;
    const screen = document.getElementById('quiz-q' + n);
    const checked = screen && screen.querySelector('input[type="radio"]:checked');
    if (!checked) return; // Next stays disabled until an option is selected.

    const idx = parseInt(checked.value, 10);
    const q = QUIZ_QUESTIONS[n - 1];
    const opt = q.options[idx];
    quizState.answers[n - 1] = { text: opt.text, states: opt.states, weight: q.weight, index: idx, id: q.id, key: opt.key || null };

    pushDataLayer('quiz_answer', { question_number: n, question_id: 'q' + n, answer_index: idx, answer_text: opt.text });

    if (n < 7) {
      quizShowQuestion(n + 1);
    } else if (quizEmailStepEnabled()) {
      quizShowEmailStep();
    } else {
      quizShowResult();
    }
  }

  // ---- /pages/quiz full-page mode + email step ---------------------------
  // sections/quiz-page.liquid sets window.STATES_QUIZ_PAGE before this file
  // runs. The modal stays the only quiz engine; page mode just shows it
  // inline. A Wingify variant can flip emailStep before DOMContentLoaded.
  function quizPageConfig() {
    return window.STATES_QUIZ_PAGE || null;
  }

  function quizEmailStepEnabled() {
    const cfg = quizPageConfig();
    return !!(cfg && cfg.emailStep && document.getElementById('quiz-email-screen'));
  }

  function quizShowEmailStep() {
    const computed = quizComputeResult(quizState.answers);
    quizState.result = computed;
    quizState.current = 8;

    const container = document.getElementById('quiz-questions-container');
    const nav = document.getElementById('quiz-nav');
    const screen = document.getElementById('quiz-email-screen');
    const matchName = document.getElementById('quiz-email-match');
    if (container) container.hidden = true;
    if (nav) nav.hidden = true;
    if (matchName) matchName.textContent = QUIZ_PRODUCTS[computed.topMatch].name;
    const sub = document.getElementById('quiz-email-sub');
    const offer = quizPageConfig().offerText || (window.quizBoxSetPromo && window.quizBoxSetPromo.emailOffer);
    if (sub && offer) sub.textContent = 'Your top match, the State it pairs with, when to take each, and ' + offer + '.';
    if (screen) screen.hidden = false;

    quizUpdateProgress(7, { allFilled: true, eyebrowText: 'YOUR MATCH IS READY', counterText: '7 / 7', srText: 'All questions answered' });
    pushDataLayer('quiz_email_step_view', { top_match: computed.topMatch, pairs_with: computed.pairsWith });

    const input = document.getElementById('quiz-email-input');
    if (input) input.focus();
  }

  // Same route as the ebook page: Shopify's customer form. The Shopify →
  // Braze sync picks the customer up; the tags carry the quiz answers.
  function quizEmailTags(result) {
    const q1 = quizState.answers[0];
    const tags = ['quiz', 'quiz-top-' + result.topMatch, 'quiz-pair-' + result.pairsWith];
    if (q1 && q1.states && q1.states[0]) tags.push('quiz-concern-' + q1.states[0]);
    const outcome = quizState.answers.filter((a) => a && a.id === 'outcome')[0];
    if (outcome && outcome.states && outcome.states[0]) tags.push('quiz-outcome-' + outcome.states[0]);
    if (window.quizBoxSetPromo) tags.push('quiz-black-friday');
    ['history', 'start', 'proof'].forEach((id) => {
      const key = quizAnswerKey(quizState.answers, id);
      if (key) tags.push('quiz-' + id + '-' + key);
    });
    return tags.join(',');
  }

  function quizSubmitEmail(e) {
    e.preventDefault();
    const form = e.target;
    const input = document.getElementById('quiz-email-input');
    const consent = document.getElementById('quiz-email-consent');
    const error = document.getElementById('quiz-email-error');
    const email = input ? input.value.trim() : '';
    const result = quizState.result;

    if (!input || !input.checkValidity() || !email) {
      if (error) { error.textContent = 'Please enter a valid email address.'; error.hidden = false; }
      return;
    }
    if (consent && !consent.checked) {
      if (error) { error.textContent = 'Tick the box so we can email your routine.'; error.hidden = false; }
      return;
    }
    if (error) error.hidden = true;

    const body = new FormData();
    body.append('form_type', 'customer');
    body.append('utf8', '✓');
    body.append('contact[email]', email);
    body.append('contact[tags]', quizEmailTags(result));

    const submitBtn = form.querySelector('button[type="submit"]');
    if (submitBtn) submitBtn.disabled = true;

    quizSendLeadWebhook(email, result);

    // Shopify answers a blocked post with a redirect to /challenge, still
    // a 200, so only an ok response that didn't land there counts.
    fetch('/contact', { method: 'POST', body: body, credentials: 'same-origin' })
      .then((res) => {
        if (res.ok && !/\/challenge/.test(res.url)) quizState.emailCaptured = true;
      })
      .catch(() => null)
      .finally(() => {
        const ok = !!quizState.emailCaptured;
        pushDataLayer('quiz_email_submit', { top_match: result.topMatch, pairs_with: result.pairsWith, delivered: ok });
        if (ok) quizPublish('states_quiz_lead', quizAnswerSummary(result));
        if (window.analytics && typeof window.analytics.track === 'function') {
          window.analytics.track('Quiz Email Submitted', { quiz_id: 'state-finder', top_match: result.topMatch, pairs_with: result.pairsWith, delivered: ok });
        }
        if (submitBtn) submitBtn.disabled = false;
        quizLeaveEmailStep();
      });
  }

  // Every quiz lead also goes to the Make webhook set on the quiz page
  // (section setting webhook_url). Form-encoded with no-cors so the browser
  // sends it without a preflight; the response can't be read, so this is
  // fire-and-forget and never blocks the result.
  function quizSendLeadWebhook(email, result) {
    const cfg = quizPageConfig();
    if (!cfg || !cfg.webhookUrl) return;
    // Only consented leads reach Make, so no scenario run is spent on others.
    const consent = document.getElementById('quiz-email-consent');
    if (!consent || !consent.checked) return;
    const summary = quizAnswerSummary(result);
    const data = new URLSearchParams();
    data.append('email', email);
    data.append('marketing_consent', 'true');
    data.append('tags', quizEmailTags(result));
    Object.keys(summary).forEach((k) => { if (summary[k]) data.append(k, summary[k]); });
    quizState.answers.forEach((a, i) => { if (a) data.append('q' + (i + 1), a.text); });
    data.append('black_friday', window.quizBoxSetPromo ? 'true' : 'false');
    data.append('page_url', window.location.href.split('#')[0]);
    data.append('submitted_at', new Date().toISOString());
    try {
      fetch(cfg.webhookUrl, { method: 'POST', mode: 'no-cors', keepalive: true, body: data }).catch(() => null);
    } catch (e) { /* never break the quiz over the webhook */ }
  }

  function quizSkipEmail() {
    pushDataLayer('quiz_email_skip', {});
    quizLeaveEmailStep();
  }

  function quizLeaveEmailStep() {
    const screen = document.getElementById('quiz-email-screen');
    if (screen) screen.hidden = true;
    quizShowResult();
  }

  function quizHandleBack() {
    const n = quizState.current;
    if (n <= 1) return;
    pushDataLayer('quiz_back', { from_question: n });
    quizShowQuestion(n - 1);
  }

  function quizShowResult() {
    // The email step already computed it; startQuiz/reset clear it.
    const computed = quizState.result || quizComputeResult(quizState.answers);
    quizState.result = computed;
    quizState.completed = true;
    quizState.current = 8;

    const container = document.getElementById('quiz-questions-container');
    const nav = document.getElementById('quiz-nav');
    const result = document.getElementById('quiz-result-screen');
    const content = document.getElementById('quiz-result-content');

    if (container) container.hidden = true;
    if (nav) nav.hidden = true;
    if (result) result.hidden = false;
    if (content) {
      content.innerHTML = (quizState.emailCaptured
        ? '<p class="quiz-email-sent" role="status">Your routine is on its way to your inbox.</p>'
        : '') + quizBuildResultHtml(computed.topMatch, computed.pairsWith, quizState.answers);
    }

    quizUpdateProgress(7, { allFilled: true, eyebrowText: 'YOUR RESULT', counterText: 'COMPLETED', srText: 'Quiz complete' });

    const stack = quizStackInfo(computed.topMatch, computed.pairsWith);
    pushDataLayer('quiz_complete', { top_match: computed.topMatch, pairs_with: computed.pairsWith, stack_name: stack.name });
    quizPublish('states_quiz_completed', quizAnswerSummary(computed));

    // Preserve existing Segment analytics (adapted to the 7-question /
    // 2-product result shape — SegmentTracking itself is untouched).
    SegmentTracking.trackQuizCompleted(
      'state-finder',
      'Find Your Optimal State',
      computed.topMatch,
      [computed.topMatch, computed.pairsWith],
      computed.scores
    );
  }

  function quizResetState() {
    const intro = document.getElementById('quiz-intro-screen');
    const container = document.getElementById('quiz-questions-container');
    const progress = document.getElementById('quiz-progress');
    const nav = document.getElementById('quiz-nav');
    const result = document.getElementById('quiz-result-screen');
    const content = document.getElementById('quiz-result-content');
    const nextBtn = document.getElementById('quiz-next-btn');
    const backBtn = document.getElementById('quiz-back-btn');

    quizState.current = 0;
    quizState.answers = [];
    quizState.started = false;
    quizState.completed = false;
    quizState.result = null;
    quizState.emailCaptured = false;

    const emailScreen = document.getElementById('quiz-email-screen');
    if (emailScreen) emailScreen.hidden = true;
    if (intro) intro.hidden = false;
    if (container) { container.hidden = true; container.innerHTML = ''; }
    if (progress) progress.hidden = true;
    if (nav) nav.hidden = true;
    if (result) result.hidden = true;
    if (content) content.innerHTML = '';
    if (nextBtn) nextBtn.disabled = true;
    if (backBtn) backBtn.hidden = true;
  }

  // ---- Modal open/close, focus trap, Esc (§7) ---------------------------
  window.openQuiz = function(source) {
    source = source || 'unknown';
    const overlay = document.getElementById('quiz-overlay');
    const card = document.getElementById('quiz-modal-card');
    if (!overlay) return;
    if (overlay.classList.contains('active')) return; // already open — avoid stacking a second focus-trap listener

    quizTriggerEl = document.activeElement;
    quizState.source = source;

    overlay.classList.add('active');
    lockScroll();

    pushDataLayer('quiz_open', { source: source });

    const focusTarget = card && card.querySelector('#quiz-close-btn, button, [href], input, select, textarea, [tabindex]');
    if (focusTarget) focusTarget.focus();

    quizFocusTrapHandler = function(e) {
      if (e.key === 'Escape') {
        closeQuizDirect();
        return;
      }
      if (e.key !== 'Tab' || !card) return;
      const rawFocusables = Array.prototype.slice.call(
        card.querySelectorAll('button:not([disabled]):not([hidden]), a[href]:not([hidden]), input:not([disabled]):not([hidden]), select, textarea, [tabindex]:not([tabindex="-1"])')
      ).filter((el) => el.offsetParent !== null);
      // A radio group is a single native Tab stop (the checked option, or the
      // first option if none is checked) — collapse each named group down to
      // that one element so first/last match what Tab actually visits. Without
      // this, an unanswered question's radios all satisfy the selector above
      // but only one of them is really reachable via Tab, so "last" is
      // miscomputed and the boundary check below never fires, leaking focus
      // out of the modal.
      const seenRadioGroups = {};
      const focusables = rawFocusables.filter((el) => {
        if (el.tagName !== 'INPUT' || el.type !== 'radio' || !el.name) return true;
        if (seenRadioGroups[el.name]) return false;
        seenRadioGroups[el.name] = true;
        const group = rawFocusables.filter((r) => r.tagName === 'INPUT' && r.type === 'radio' && r.name === el.name);
        const checked = group.find((r) => r.checked);
        return el === (checked || group[0]);
      });
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', quizFocusTrapHandler);
  };

  window.closeQuiz = function(e) {
    if (quizPageConfig()) return; // inline on /pages/quiz: nothing to close
    if (e && e.target === document.getElementById('quiz-overlay')) {
      closeQuizDirect();
    }
  };

  window.closeQuizDirect = function() {
    const overlay = document.getElementById('quiz-overlay');
    if (!overlay || quizPageConfig()) return;

    // Abandon tracking: quiz opened past intro and not yet completed.
    if (quizState.started && !quizState.completed) {
      pushDataLayer('quiz_abandon', { last_question: quizState.current });
    }

    overlay.classList.remove('active');
    unlockScroll();

    if (quizFocusTrapHandler) {
      document.removeEventListener('keydown', quizFocusTrapHandler);
      quizFocusTrapHandler = null;
    }
    if (quizTriggerEl && typeof quizTriggerEl.focus === 'function') {
      quizTriggerEl.focus();
    }
    quizTriggerEl = null;

    // Reset-on-close (PRD §12 resolution #6 — matches prior behavior).
    quizResetState();
  };

  window.startQuiz = function() {
    const intro = document.getElementById('quiz-intro-screen');
    const container = document.getElementById('quiz-questions-container');
    const progress = document.getElementById('quiz-progress');
    const nav = document.getElementById('quiz-nav');
    if (!intro || !container) return;

    quizState.started = true;
    quizState.completed = false;
    quizState.answers = [];
    quizState.result = null;

    intro.hidden = true;
    container.hidden = false;
    if (progress) progress.hidden = false;
    if (nav) nav.hidden = false;

    quizRenderQuestions();
    quizShowQuestion(1);

    pushDataLayer('quiz_start', {});
    SegmentTracking.trackQuizStarted('state-finder', 'Find Your Optimal State');
  };

  window.retakeQuiz = function() {
    pushDataLayer('quiz_retake', {});
    // Resets back to the intro screen (PRD §5.2 Screen 8 footer: "Retake
    // the quiz — resets to Intro"); the user clicks "Start the quiz" again.
    quizResetState();
  };

  // Disables a result-screen button for the duration of an async cart
  // operation so a slow network + repeat clicks can't add duplicate items,
  // then restores its original label/enabled state once settled.
  function quizRunWithButtonLock(btn, run) {
    const originalText = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Adding…';
    Promise.resolve(run()).finally(() => {
      btn.disabled = false;
      btn.textContent = originalText;
    });
  }

  // window.addToCart(handle) — reused EXACTLY as it existed before this
  // rebuild; do not reimplement cart logic here.
  window.addToCart = function(handle) {
    if (window.quizProductIds && window.quizProductIds[handle]) {
      return Cart.addToCartById(window.quizProductIds[handle], 1, 'quiz');
    }
    console.warn('addToCart: variant ID not found for handle', handle);
    return Promise.resolve();
  };

  window.handleQuizAddToCart = function(handle, context) {
    pushDataLayer('quiz_add_to_cart', { product: handle, context: context });
    return window.addToCart(handle);
  };

  window.shopYourStack = function() {
    const result = quizState.result;
    if (!result || !result.topMatch || !result.pairsWith) return Promise.resolve();
    // Sequential, not parallel — chained on the returned promise so the
    // second /cart/add.js + cart-drawer refresh only starts once the first
    // has actually resolved, instead of racing it.
    return window.handleQuizAddToCart(result.topMatch, 'stack')
      .then(() => window.handleQuizAddToCart(result.pairsWith, 'stack'));
  };

  // ---- Static nav / result-action bindings (bound once) -----------------
  function quizInitBindings() {
    const nextBtn = document.getElementById('quiz-next-btn');
    const backBtn = document.getElementById('quiz-back-btn');
    const questionsContainer = document.getElementById('quiz-questions-container');

    if (nextBtn) nextBtn.addEventListener('click', quizHandleNext);
    if (backBtn) backBtn.addEventListener('click', quizHandleBack);

    if (questionsContainer) {
      questionsContainer.addEventListener('change', (e) => {
        const radio = e.target;
        if (!radio || radio.type !== 'radio') return;
        const screen = radio.closest('.quiz-question-screen');
        if (screen) {
          screen.querySelectorAll('.quiz-answer-card').forEach((c) => c.classList.remove('is-selected'));
        }
        const card = radio.closest('.quiz-answer-card');
        if (card) card.classList.add('is-selected');
        if (nextBtn) nextBtn.disabled = false;
      });
    }

    // Delegated clicks for content injected into #quiz-result-content
    // (per-card Add to Cart, shop-stack, upsell link, retake).
    //
    // Bound on #quiz-modal-card (not document): #quiz-modal-card has
    // onclick="event.stopPropagation()" (snippets/quiz-modal.liquid) so real,
    // bubbling clicks inside the modal never reach document. A delegated
    // listener on document would silently never fire for any button in here.
    const modalCard = document.getElementById('quiz-modal-card');
    if (modalCard) {
      modalCard.addEventListener('click', (e) => {
        const atcBtn = e.target.closest('.quiz-result-atc');
        if (atcBtn) {
          if (atcBtn.disabled) return; // already adding — ignore repeat clicks
          const card = atcBtn.closest('.quiz-result-card');
          const context = card && card.id === 'quiz-result-top-match' ? 'top_match' : 'pairs_with';
          quizRunWithButtonLock(atcBtn, () => window.handleQuizAddToCart(atcBtn.dataset.handle, context));
          return;
        }
        const boxSetBtn = e.target.closest('#quiz-boxset-btn');
        if (boxSetBtn) {
          if (boxSetBtn.disabled) return;
          quizRunWithButtonLock(boxSetBtn, () => window.handleQuizAddToCart('box-set', 'black_friday'));
          return;
        }
        const shopStackBtn = e.target.closest('#quiz-shop-stack-btn');
        if (shopStackBtn) {
          if (shopStackBtn.disabled) return;
          quizRunWithButtonLock(shopStackBtn, () => window.shopYourStack());
          return;
        }
        if (e.target.closest('#quiz-retake-btn')) {
          window.retakeQuiz();
          return;
        }
        if (e.target.closest('#quiz-email-skip')) {
          quizSkipEmail();
          return;
        }
      });
    }

    const emailForm = document.getElementById('quiz-email-form');
    if (emailForm) emailForm.addEventListener('submit', quizSubmitEmail);

    // /pages/quiz: show the quiz inline instead of as a modal (no scroll
    // lock, no focus trap, close button hidden by the section's CSS).
    if (quizPageConfig()) {
      const overlay = document.getElementById('quiz-overlay');
      const mount = document.getElementById('quiz-page-mount');
      if (overlay) {
        if (mount) mount.appendChild(overlay); // layout renders it after the footer
        document.body.classList.add('quiz-page-mode');
        overlay.classList.add('active');
        overlay.removeAttribute('aria-modal');
        quizState.source = 'quiz_page';
        pushDataLayer('quiz_open', { source: 'quiz_page' });
      }
    }
  }

  // ==========================================
  // Initialize All Modules
  // ==========================================
  
  document.addEventListener('DOMContentLoaded', () => {
    Cart.init();
    ProductGallery.init();
    QuantitySelector.init();
    PricingOptions.init();
    FAQ.init();
    TestimonialsFilter.init();
    quizInitBindings();
    MobileMenu.init();
    AnnouncementBar.init();
    VideoPlayer.init();
    SmoothScroll.init();
  });

  // Expose SegmentTracking globally for use in Liquid templates
  window.SegmentTracking = SegmentTracking;

  // ==========================================
  // Mindvalley OAuth Login Function
  // ==========================================
  
  /**
   * Redirects to Mindvalley OAuth login page
   * Can be called programmatically from buttons, links, or conditional logic
   */
  function gotoMindvalleyLogin() {
    // Generate a cryptographically random state token to prevent CSRF on the
    // OAuth redirect. The state is stored in sessionStorage and should be
    // verified by whatever server-side handler processes the callback.
    let state = '';
    try {
      state = Array.from(crypto.getRandomValues(new Uint8Array(16)))
        .map(b => b.toString(16).padStart(2, '0')).join('');
      sessionStorage.setItem('mv_oauth_state', state);
    } catch (e) {
      state = Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);
    }
    const params = new URLSearchParams({
      response_type: 'code',
      client_id: 'aSVRkkinfpylyTeKHkLQKbPkaJw29OHg',
      state: state
    });
    window.location.href = 'https://login.mindvalley.com/authorize?' + params.toString();
  }

  // Expose globally for use throughout the theme
  window.gotoMindvalleyLogin = gotoMindvalleyLogin;

  // ==========================================
  // Countdown Timer Web Component
  // ==========================================
  
  /**
   * Countdown Timer Custom Element
   * Renders a countdown timer that updates every second
   * and handles expiration behavior (hide section or show message)
   */
  class CountdownTimer extends HTMLElement {
    constructor() {
      super();

      // Selectors
      this.selectors = {
        time: 'time',
        days: '[data-days]',
        hours: '[data-hours]',
        minutes: '[data-minutes]',
        seconds: '[data-seconds]',
        shopifySection: '.shopify-section',
      };

      // Settings
      this.settings = {
        hideSection: 'hide-section',
        showMessage: 'show-message',
      };

      // Get DOM elements
      this.shopifySection = this.closest(this.selectors.shopifySection);
      this.expirationBehavior = this.getAttribute('data-expiration-behavior');
      
      this.time = this.querySelector(this.selectors.time);
      this.days = this.querySelector(this.selectors.days);
      this.hours = this.querySelector(this.selectors.hours);
      this.minutes = this.querySelector(this.selectors.minutes);
      this.seconds = this.querySelector(this.selectors.seconds);

      // Guard against missing <time> element — falls through to onComplete()
      this.endDate = this.time ? Date.parse(this.time.dateTime) : NaN;
      this.daysInMs = 1000 * 60 * 60 * 24;
      this.hoursInMs = this.daysInMs / 24;
      this.minutesInMs = this.hoursInMs / 60;
      this.secondsInMs = this.minutesInMs / 60;

      this.shouldHideOnComplete = this.expirationBehavior === this.settings.hideSection;
      this.shouldShowMessage = this.expirationBehavior === this.settings.showMessage;

      this.update = this.update.bind(this);
    }

    connectedCallback() {
      if (isNaN(this.endDate)) {
        this.onComplete();
        return;
      }

      if (this.endDate <= Date.now()) {
        this.onComplete();
        return;
      }

      // Update the countdown every second
      this.interval = setInterval(this.update, 1000);
    }

    disconnectedCallback() {
      this.stopTimer();
    }

    convertTime(timeInMs) {
      const days = this.formatDigits(parseInt(timeInMs / this.daysInMs, 10));
      timeInMs -= days * this.daysInMs;

      const hours = this.formatDigits(parseInt(timeInMs / this.hoursInMs, 10));
      timeInMs -= hours * this.hoursInMs;

      const minutes = this.formatDigits(parseInt(timeInMs / this.minutesInMs, 10));
      timeInMs -= minutes * this.minutesInMs;

      const seconds = this.formatDigits(parseInt(timeInMs / this.secondsInMs, 10));

      return {
        days: days,
        hours: hours,
        minutes: minutes,
        seconds: seconds,
      };
    }

    // Make numbers less than 10 to appear with a leading zero like 01, 02, 03
    formatDigits(number) {
      if (number < 10) number = '0' + number;
      return number;
    }

    render(timer) {
      this.days.textContent = timer.days;
      this.hours.textContent = timer.hours;
      this.minutes.textContent = timer.minutes;
      this.seconds.textContent = timer.seconds;
    }

    stopTimer() {
      clearInterval(this.interval);
    }

    onComplete() {
      this.render({
        days: '00',
        hours: '00',
        minutes: '00',
        seconds: '00',
      });

      if (this.shouldHideOnComplete && this.shopifySection) {
        this.shopifySection.classList.add('hidden');
        this.shopifySection.style.display = 'none';
      }

      if (this.shouldShowMessage) {
        this.classList.add('show-message');
      }
    }

    // Function to update the countdown
    update() {
      const timeNow = new Date().getTime();
      const timeDiff = this.endDate - timeNow;

      if (timeDiff <= 0) {
        this.stopTimer();
        this.onComplete();
        return;
      }

      const timeRemaining = this.convertTime(timeDiff);
      this.render(timeRemaining);
    }
  }

  // Register the custom element
  if (!customElements.get('countdown-timer')) {
    customElements.define('countdown-timer', CountdownTimer);
  }

  // ==========================================
  // Scroll Reveal Animation System
  // ==========================================

  const ScrollReveal = {
    init() {
      // Add reveal classes to sections
      this.observeSections();
      this.observeElements();
      this.initHeroAnimation();
      this.initParallax();
    },

    observeSections() {
      // Exclude the header's wrapper: `.reveal-section.revealed` sets
      // `transform: translateY(0)` — even an identity transform creates a
      // new containing block for descendants, which silently breaks
      // `.site-header`'s `position: fixed` (it renders correctly at
      // scrollY 0 but then scrolls away with the page instead of staying
      // pinned, since it's no longer positioned against the real
      // viewport). A persistent fixed nav has no business fading in on
      // scroll anyway, so it's simplest to just never observe it.
      const sections = document.querySelectorAll(
        // Exclude header (fixed nav) and product-hero (above-the-fold — global
        // .reveal-section starts at opacity:0 and races LCP / FOUC). Homepage
        // .hero is separately forced visible in brand-system.css; product-hero is not.
        'section:not(:has(.site-header)):not(.product-hero):not(:has(.product-hero)), .shopify-section:not(:has(.site-header)):not(:has(.product-hero))'
      );

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
            // Stagger children animations
            const children = entry.target.querySelectorAll('.reveal-item');
            children.forEach((child, index) => {
              child.style.transitionDelay = `${index * 0.1}s`;
              child.classList.add('revealed');
            });
            observer.unobserve(entry.target);
          }
        });
      }, {
        /* 0.01: tall sections (e.g. mobile testimonials ~7500px) never reach
           0.1 intersection ratio and stayed invisible (2026-07-21 mobile audit). */
        threshold: 0.01,
        rootMargin: '0px 0px -50px 0px'
      });

      sections.forEach(section => {
        if (section.classList.contains('product-hero') || section.querySelector('.product-hero')) return;
        section.classList.add('reveal-section');
        observer.observe(section);
      });
    },

    observeElements() {
      const elements = document.querySelectorAll(
        '.hero__title, .hero__subtitle, .hero__ctas, .hero__stats, ' +
        'h1, h2, h3, .product-card, .testimonial-card, .stat-item, ' +
        '.feature-item, .benefit-item'
      );

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
            observer.unobserve(entry.target);
          }
        });
      }, {
        /* 0.01, no negative rootMargin: matches observeSections() above —
           a stricter 0.2 + -100px margin left headings inside tall sections
           (e.g. product-faq) invisible well after their section background
           had already revealed, reading as a large blank gap mid-scroll. */
        threshold: 0.01,
        rootMargin: '0px 0px -20px 0px'
      });

      elements.forEach((el, index) => {
        // Product hero headline/CTAs are above the fold — don't start them at opacity:0.
        if (el.closest('.product-hero')) return;
        el.classList.add('animate-on-scroll');
        el.style.setProperty('--animation-order', index % 5);
        observer.observe(el);
      });
    },

    initHeroAnimation() {
      const hero = document.querySelector('.hero');
      if (!hero) return;

      setTimeout(() => {
        hero.classList.add('hero-animated');
      }, 100);

      // Skip parallax on touch devices / small viewports — transforming on every
      // scroll tick is a known cause of janky mobile scroll.
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const isTouchOrSmall = window.matchMedia('(max-width: 768px)').matches || ('ontouchstart' in window);
      if (prefersReducedMotion || isTouchOrSmall) return;

      let ticking = false;
      window.addEventListener('scroll', () => {
        if (!ticking) {
          requestAnimationFrame(() => {
            const scrolled = window.pageYOffset;
            const heroContent = hero.querySelector('.hero__content');
            const heroMedia = hero.querySelector('.hero__media');

            if (heroContent && scrolled < window.innerHeight) {
              heroContent.style.transform = `translateY(${scrolled * 0.3}px)`;
              heroContent.style.opacity = Math.max(0, 1 - (scrolled / (window.innerHeight * 0.8)));
            }
            if (heroMedia && scrolled < window.innerHeight) {
              heroMedia.style.transform = `translateY(${scrolled * 0.15}px) scale(${1 - scrolled * 0.0003})`;
            }
            ticking = false;
          });
          ticking = true;
        }
      }, { passive: true });
    },

    initParallax() {
      const parallaxElements = document.querySelectorAll('[data-parallax]');
      if (!parallaxElements.length) return;

      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const isTouchOrSmall = window.matchMedia('(max-width: 768px)').matches || ('ontouchstart' in window);
      if (prefersReducedMotion || isTouchOrSmall) return;

      let ticking = false;
      window.addEventListener('scroll', () => {
        if (!ticking) {
          requestAnimationFrame(() => {
            const scrolled = window.pageYOffset;
            parallaxElements.forEach(el => {
              const speed = parseFloat(el.dataset.parallax) || 0.5;
              el.style.transform = `translateY(${-(scrolled * speed)}px)`;
            });
            ticking = false;
          });
          ticking = true;
        }
      }, { passive: true });
    }
  };

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => ScrollReveal.init());
  } else {
    ScrollReveal.init();
  }

})();


